import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, AlertTriangle, CheckCircle, Info, XCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export type ConfirmationVariant = 'danger' | 'warning' | 'info' | 'success';

interface ConfirmationDrawerProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    variant?: ConfirmationVariant;
    loading?: boolean;
}

const variantConfig = {
    danger: {
        icon: XCircle,
        iconColor: 'text-red-600',
        bgGradient: 'from-red-50 to-rose-50',
        borderColor: 'border-red-200',
        buttonBg: 'from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700',
        iconBg: 'bg-red-100',
    },
    warning: {
        icon: AlertTriangle,
        iconColor: 'text-yellow-600',
        bgGradient: 'from-yellow-50 to-amber-50',
        borderColor: 'border-yellow-200',
        buttonBg: 'from-yellow-600 to-amber-600 hover:from-yellow-700 hover:to-amber-700',
        iconBg: 'bg-yellow-100',
    },
    info: {
        icon: Info,
        iconColor: 'text-blue-600',
        bgGradient: 'from-blue-50 to-indigo-50',
        borderColor: 'border-blue-200',
        buttonBg: 'from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700',
        iconBg: 'bg-blue-100',
    },
    success: {
        icon: CheckCircle,
        iconColor: 'text-green-600',
        bgGradient: 'from-green-50 to-emerald-50',
        borderColor: 'border-green-200',
        buttonBg: 'from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700',
        iconBg: 'bg-green-100',
    },
};

export default function ConfirmationDrawer({
    open,
    onClose,
    onConfirm,
    title,
    message,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    variant = 'danger',
    loading = false,
}: ConfirmationDrawerProps) {
    const [mounted, setMounted] = useState(false);
    const [animate, setAnimate] = useState(false);

    const config = variantConfig[variant];
    const Icon = config.icon;

    useEffect(() => {
        if (open) {
            setMounted(true);
            setTimeout(() => setAnimate(true), 10);
            document.body.classList.add('overflow-hidden');
        } else {
            setAnimate(false);
            document.body.classList.remove('overflow-hidden');
            setTimeout(() => setMounted(false), 300);
        }

        return () => {
            document.body.classList.remove('overflow-hidden');
        };
    }, [open]);

    if (!mounted) return null;

    /** Above RightDrawer (z-[99999]) and other modals; portal to body avoids transform/stacking from parent panels */
    const overlay = (
        <div className="fixed inset-0 z-[100100]">
            {/* Backdrop */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: animate ? 1 : 0 }}
                transition={{ duration: 0.3 }}
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Drawer Wrapper with Margin */}
            <div className="absolute top-[10px] bottom-[10px] right-[10px] left-[10px] z-[100101] flex justify-end pointer-events-none">
                {/* Drawer Panel */}
                <motion.div
                    initial={{ x: '100%' }}
                    animate={{ x: animate ? 0 : '100%' }}
                    transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                    className="bg-white shadow-2xl overflow-hidden h-full w-full sm:w-[500px] pointer-events-auto rounded-xl flex flex-col"
                >
                    {/* Header */}
                    <div className="flex justify-between items-center p-6 border-b border-gray-200">
                        <h2 className="text-xl font-bold text-gray-900">{title}</h2>
                        <button
                            onClick={onClose}
                            disabled={loading}
                            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-6">
                        <div className={`bg-gradient-to-br ${config.bgGradient} border-2 ${config.borderColor} rounded-2xl p-6`}>
                            <div className="flex items-start gap-4">
                                <div className={`${config.iconBg} rounded-full p-3 flex-shrink-0`}>
                                    <Icon className={`w-8 h-8 ${config.iconColor}`} />
                                </div>
                                <div className="flex-1">
                                    <p className="text-gray-800 text-base leading-relaxed whitespace-pre-line">
                                        {message}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="p-6 border-t border-gray-200 bg-gray-50">
                        <div className="flex items-center gap-3 justify-end">
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={onClose}
                                disabled={loading}
                                className="px-6 py-2.5 text-sm font-semibold text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                            >
                                {cancelText}
                            </motion.button>
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={onConfirm}
                                disabled={loading}
                                className={`px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r ${config.buttonBg} rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg`}
                            >
                                {loading ? (
                                    <span className="flex items-center gap-2">
                                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                        Processing...
                                    </span>
                                ) : (
                                    confirmText
                                )}
                            </motion.button>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    );

    return typeof document !== 'undefined' ? createPortal(overlay, document.body) : overlay;
}
